import { AxiosResponse } from "axios";
import {
    CountrySlugs,
    UTSLocale,
    UTSLOCALE_TO_COUNTRY_CODE,
} from "@smile2impress/attraction-constants";
import { type MapboxFeatureCollectionType } from "@smile2impress/clinics-map";
import {
    GetAttractionClinicsFilter,
    GetClinicByClinicIdResponse,
    GetClinicByClinicIdType,
    GetAttractionClinicsResponse,
} from "api/location";
import { axiosInstance, axiosInstanceV2 } from "api/global";
import { handleApiError } from "api/helpers";

import {
    UserLocationCityResponse,
    CityWithClinicsAndTimeslotsResponse,
    GetClinicsByPostcodeResponse,
    DistanceType,
    CityByGeo,
    CityWithClinicsAndTimeslotsType,
} from "./location.types";

// TODO AD-2185: DK uses SE (Malmö) until separate location available
const getLocationApiCountrySlug = (locale: UTSLocale): string =>
    locale === UTSLocale.DENMARK_DK
        ? CountrySlugs[UTSLocale.SWEDEN_SE]
        : CountrySlugs[locale];

export const fetchUserLocationCity = async () =>
    await axiosInstance
        .get("/user-region-service/v1/region-lang")
        .then((response: AxiosResponse<UserLocationCityResponse>) => {
            return response.data.result.city;
        })
        .catch(handleApiError);

export const fetchClinicsWithTimeslotsByCityId = async (
    cityId: number
): Promise<CityWithClinicsAndTimeslotsType> =>
    await axiosInstanceV2
        .get(`/attraction-service/v1/clinics/cities/${cityId}`)
        .then(
            (response: AxiosResponse<CityWithClinicsAndTimeslotsResponse>) => {
                return response.data.data;
            }
        )
        .catch(handleApiError);

export const fetchClinicById = async ({ clinicId }: GetClinicByClinicIdType) =>
    await axiosInstanceV2
        .get(`/attraction-service/locations/v1/clinics/${clinicId}`)
        .then((response: AxiosResponse<GetClinicByClinicIdResponse>) => {
            return response.data;
        })
        .catch(handleApiError);

export const fetchClinics = async (locale: UTSLocale, postcode: string) => {
    const queryParams = new URLSearchParams({
        postcode,
        units: DistanceType.MI,
        distance: "150",
    });

    return await axiosInstanceV2
        .get(
            `/attraction-service/locations/v1/country/${getLocationApiCountrySlug(
                locale
            )}/clinicsByPostcode`,
            { params: queryParams }
        )
        .then((response: AxiosResponse<GetClinicsByPostcodeResponse>) => {
            return response.data.data;
        })
        .catch(handleApiError);
};

export const fetchAttractionClinics = async (
    filter: GetAttractionClinicsFilter
): Promise<GetAttractionClinicsResponse> => {
    const queryParams = new URLSearchParams();
    if (filter.confirmationType) {
        queryParams.append("confirmationType", filter.confirmationType);
    }

    return await axiosInstanceV2
        .get(`/attraction-service/v1/attraction-clinics`, {
            params: queryParams,
        })
        .then(({ data }: AxiosResponse<GetAttractionClinicsResponse>) => data)
        .catch(handleApiError);
};

export const getCityByGeo = async (
    country: string,
    latitude: string,
    longitude: string
): Promise<CityByGeo | null> => {
    try {
        const cityRequest = await fetch(
            `/attraction-service/locations/v1/country/${country}/cityByCoordinates?latitude=${latitude}&longitude=${longitude}`
        );

        const cityData = await cityRequest.json();
        return cityData.data;
    } catch (e) {
        return null;
    }
};

export const searchLocation = async (
    searchQuery: string,
    locale: UTSLocale
): Promise<MapboxFeatureCollectionType> =>
    await axiosInstanceV2
        .get(
            `https://api.mapbox.com/search/geocode/v6/forward?q=${encodeURIComponent(
                searchQuery
            )}&access_token=${
                process.env.MAPBOX_API_KEY || ""
            }&types=place,address,postcode,locality&autocomplete=true&limit=5&country=${
                UTSLOCALE_TO_COUNTRY_CODE[locale]
            }`
        )
        .then((response: AxiosResponse<MapboxFeatureCollectionType>) => {
            return response.data;
        })
        .catch(handleApiError);
